import { useEffect, useState } from "react";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";

/**
 * Patient-facing name of an appointment type: the practice's description
 * ("Kontrolluntersuchung") when present, otherwise its label. The label is
 * often an internal short code ("KU", "BH") and is only a fallback (KO-60).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * appointmentTypeDisplayName({ label: "KU", description: "Kontrolluntersuchung" }); // "Kontrolluntersuchung"
 * ```
 */
export function appointmentTypeDisplayName(type: {
  readonly label: string;
  readonly description?: string;
}): string {
  const description = type.description?.trim();
  return description !== undefined && description !== "" ? description : type.label;
}

/**
 * Resolves the patient-facing appointment type name for the review and done
 * screens (KO-66, KO-67). Uses the name captured when the patient chose the
 * type; when the questionnaire routed straight to the slots (no type tile was
 * tapped) it reads the type list once. Returns `undefined` until known, or
 * when the type cannot be resolved.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const typeName = useAppointmentTypeName(appointmentTypeId, capturedName);
 * ```
 */
export function useAppointmentTypeName(
  appointmentTypeId: string | undefined,
  capturedName: string | undefined,
): string | undefined {
  const [resolved, setResolved] = useState<{ id: string; name: string } | null>(null);

  useEffect(
    function resolveAppointmentTypeName() {
      if (capturedName !== undefined || appointmentTypeId === undefined) return;
      let active = true;
      void docCirrusRpcClient.frontdesk.getAppointmentTypes({}).then(
        (types) => {
          const match = types.find((type) => type.id === appointmentTypeId);
          if (active && match) {
            setResolved({ id: appointmentTypeId, name: appointmentTypeDisplayName(match) });
          }
          return undefined;
        },
        (error: unknown) => {
          frontdeskDiagnosticLogger.error("Appointment type name could not be resolved", { error });
          return undefined;
        },
      );
      return function ignoreStaleAppointmentTypeName() {
        active = false;
      };
    },
    [appointmentTypeId, capturedName],
  );

  if (capturedName !== undefined) return capturedName;
  return resolved !== null && resolved.id === appointmentTypeId ? resolved.name : undefined;
}
